import { AlertTriangle, ArrowRight, Hourglass, RefreshCw, RotateCcw, XCircle } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { api, isApiError, type DomainDetail, type RegistrantProblem, type StoppedRegistration } from '../../api';
import { Badge } from '../../components/Badge';
import { TechnicalDetail } from '../../components/States';
import { formatDateTime, formatMoney } from '../../lib/format';
import { newIdempotencyKey } from '../../lib/ids';
import { useApi } from '../../lib/useApi';
import { useLabels } from '../../lib/labels';
import { isExtraProblem } from '../../lib/extraFields';
import { readinessFor, subjectsToEdit, type VerificationAnswer } from '../../lib/readiness';
import { domainTabUrl } from './tabs/moved';
import { Money, useVocab } from './refs';
import { errorText } from '../../lib/errorText';
import { cancelTextKeys, refusalText, type RefusedCommandText } from './registration';

type Decision = 'retry' | 'cancel' | 'reactivate';

/** A registration staff can decide on: stopped, or waiting for its dossier ("Chờ định danh"). */
export function decidableStop(stop: StoppedRegistration | null | undefined): stop is StoppedRegistration {
  return Boolean(stop && (stop.state === 'MANUAL_REVIEW' || (stop.state === 'WAITING' && stop.waiting_for)));
}

/** A failed registration the server offers to reactivate on the same order ("Kích hoạt lại đơn"). */
export function reactivableStop(stop: StoppedRegistration | null | undefined): stop is StoppedRegistration {
  return Boolean(stop && stop.state === 'FAILED' && stop.reactivation);
}

/** Link to edit the subject a registrant problem is about (opens the subject's edit form). */
export function editSubjectLink(subjectId: string): string {
  return `/subjects/${encodeURIComponent(subjectId)}?edit=1`;
}

/** The fields to complete, each with what to do (no action per line). */
export function ProblemList({ problems }: { problems: RegistrantProblem[] }) {
  return (
    <ul className="grid gap-1.5" data-problem-list>
      {problems.map((p) => (
        <li key={`${p.subject_id}-${p.contact}-${p.field}`} className="flex flex-wrap items-baseline gap-x-2 text-sm">
          <span className="font-medium">{p.label}</span>
          <span className="text-muted-foreground">{p.message}</span>
        </li>
      ))}
    </ul>
  );
}

/** One "Sửa chủ thể" for the whole list (and "Sửa người đại diện" when the representative
 * lacks something). */
export function EditSubjectActions({ problems }: { problems: RegistrantProblem[] }) {
  const { t } = useTranslation('domains');
  const subjects = subjectsToEdit(problems);
  if (!subjects.length) return null;
  return (
    <div className="flex flex-wrap gap-3 text-sm">
      {subjects.map((s) => (
        <Link key={s.id} to={editSubjectLink(s.id)} className="font-medium text-primary hover:underline">
          {s.contact === 'REPRESENTATIVE' ? t('stop.editRepresentative') : t('stop.editSubject')}
        </Link>
      ))}
    </div>
  );
}

/**
 * The fields a registry would refuse, each with what to do, then one link to edit the subject —
 * and, for the extra data the TLD requires (`domain`: a domain that exists), one to enter it on
 * the domain.
 */
export function RegistrantProblems({ problems, domain }: { problems: RegistrantProblem[]; domain?: string }) {
  const { t } = useTranslation('domains');
  return (
    <div className="grid gap-2">
      <ProblemList problems={problems} />
      <div className="flex flex-wrap gap-3">
        <EditSubjectActions problems={problems} />
        {domain && problems.some(isExtraProblem) ? (
          <Link to={domainTabUrl(domain, 'subject', 'extra-fields')} className="text-sm font-medium text-primary hover:underline" data-extra-todo>
            {t('extra.todoLink')}
          </Link>
        ) : null}
      </div>
    </div>
  );
}

/**
 * A refused registry command in Vietnamese ([`refusalText`]), its own text (the registry's or the
 * connection's) folded as the technical detail — in the stop panel and on the Registry tab.
 */
export function RefusalText({ command, said }: { command: RefusedCommandText; said?: string }) {
  const { text, detail } = refusalText(command, errorText, said);
  return (
    <>
      {text ? <div className="mt-0.5">{text}</div> : null}
      <TechnicalDetail detail={detail} />
    </>
  );
}

/** One line for the refused registry command (operation, code, sent or not), then [`RefusalText`]. */
export function RefusedCommandLine({ stop }: { stop: StoppedRegistration }) {
  const { t } = useTranslation('domains');
  const L = useLabels();
  const r = stop.refused_command;
  if (!r) return null;
  return (
    <div className="text-sm">
      <span className="font-medium">{t('stop.refused')}</span> {L.eppCommand(r.operation)}
      {r.result_code !== null ? <code className="ml-1 rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{r.result_code}</code> : null}
      {r.not_sent ? <span className="text-muted-foreground"> · {t('stop.notSent')}</span> : null}
      {r.at ? <span className="text-muted-foreground"> · {formatDateTime(r.at)}</span> : null}
      <RefusalText command={r} said={stop.reason} />
    </div>
  );
}

/**
 * A registration stopped for staff (the first item of "Cần chú ý"): why, the refused command,
 * what to fix on the subject, the funds held, the related work item, and "Thử lại" /
 * "Huỷ đăng ký" — or, failed, "Kích hoạt lại đơn" with what it holds again and the wallet.
 */
export function RegistrationStopPanel({ detail, onChanged }: { detail: DomainDetail; onChanged?: () => void }) {
  const { t } = useTranslation('domains');
  const stop = detail.registration;
  const [open, setOpen] = useState<Decision | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [problems, setProblems] = useState<RegistrantProblem[] | null>(null);
  // One key per reactivation dialog: a resent request holds the funds once.
  const [key, setKey] = useState('');
  const vocab = useVocab();
  if (!stop || !(decidableStop(stop) || reactivableStop(stop))) return null;
  const waiting = stop.state === 'WAITING';
  const failed = stop.state === 'FAILED';
  const offer = failed ? stop.reactivation : null;
  // Why "Kích hoạt lại đơn" cannot be done now: the server's reason, else the wallet.
  const blocked = offer && !stop.can_reactivate ? (offer.refusal ?? t('stop.reactivateShort')) : null;
  const reusable = stop.reusable_verification ?? null;
  const shown = problems ?? stop.registrant_problems;
  const cancelText = cancelTextKeys(stop.held);
  // Each decision starts with an empty reason: one typed for "retry" is not a reason to cancel.
  const decide = (d: Decision) => {
    setReason('');
    setKey(newIdempotencyKey());
    setOpen(d);
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!open) return;
    setBusy(true);
    try {
      if (open === 'retry') await api.retryRegistration(detail.domain.id, { reason });
      else if (open === 'reactivate') await api.reactivateRegistration(detail.domain.id, { reason }, { idempotencyKey: key });
      else await api.cancelRegistration(detail.domain.id, { reason });
      toast.success(open === 'retry' ? t('stop.retried') : open === 'reactivate' ? t('stop.reactivated') : t(cancelText.done));
      setOpen(null);
      setReason('');
      onChanged?.();
    } catch (err) {
      if (isApiError(err) && err.code === 'REGISTRANT_INCOMPLETE') {
        const fields = (err.details as { fields?: RegistrantProblem[] } | undefined)?.fields;
        setProblems(fields ?? []);
        setOpen(null);
      }
      toast.error(isApiError(err) ? errorText(err).text : t('stop.failed'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={waiting ? 'grid gap-3 rounded-lg border border-warning/40 bg-warning/5 p-4' : 'grid gap-3 rounded-lg border border-critical/30 bg-critical/5 p-4'} data-registration-stop={waiting ? 'waiting' : 'stopped'}>
      <div className="flex flex-wrap items-start gap-3">
        {waiting ? <Hourglass className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden="true" /> : <AlertTriangle className="mt-0.5 size-4 shrink-0 text-critical" aria-hidden="true" />}
        <div className="min-w-[14rem] flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={waiting ? 'warn' : 'serious'}>{waiting ? t('stop.waitingTitle') : failed ? t('stop.failedTitle') : t('stop.title')}</Badge>
            <span className="text-xs text-muted-foreground">{formatDateTime(stop.stopped_at)}</span>
          </div>
          <p className="mt-1 text-sm">{stop.reason}</p>
        </div>
      </div>
      <RefusedCommandLine stop={stop} />
      {waiting && reusable ? (
        <p className="text-sm">
          {t('stop.reusable', { method: vocab('method', reusable.method), when: formatDateTime(reusable.verified_at) })}
        </p>
      ) : null}
      {stop.vnnic_reason ? (
        <p className="text-sm break-words">
          <span className="font-medium">{t('stop.vnnicReason')}</span> {stop.vnnic_reason}
        </p>
      ) : null}
      {shown.length ? (
        <div>
          <div className="mb-1 text-sm font-medium">{t('stop.todo')}</div>
          <RegistrantProblems problems={shown} domain={detail.domain.ascii_name} />
        </div>
      ) : stop.subject_changed_since ? (
        <p className="text-sm">{t('stop.subjectChanged')}</p>
      ) : null}
      {offer ? (
        <div className="grid gap-1 text-sm" data-reactivation>
          <p className="flex flex-wrap gap-x-3">
            <span>
              {t('stop.reactivateHolds')} <Money amount={offer.amount} currency={offer.currency} />
            </span>
            <span>
              {t('stop.walletAvailable')} <Money amount={offer.available} currency={offer.currency} />
            </span>
          </p>
          {blocked ? <p className="text-muted-foreground">{blocked}</p> : null}
        </div>
      ) : stop.held ? (
        <p className="text-sm">
          {t('stop.held')} <Money amount={stop.held.amount} currency={stop.held.currency} />
          {stop.held.status !== 'ACTIVE' ? <span className="text-muted-foreground"> · {stop.held.status}</span> : null}
        </p>
      ) : null}
      {stop.work_items.length ? (
        <p className="flex flex-wrap gap-x-3 text-sm">
          {stop.work_items.map((w) => (
            <Link key={w.id} to={`/compliance/work/${encodeURIComponent(w.id)}`} className="text-primary hover:underline">
              {t('stop.workItem')} <ArrowRight className="inline size-3" aria-hidden="true" />
            </Link>
          ))}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {stop.can_retry ? (
          <Button size="sm" onClick={() => decide('retry')}>
            <RotateCcw /> {t('stop.retry')}
          </Button>
        ) : null}
        {stop.can_cancel ? (
          <Button size="sm" variant="outline" onClick={() => decide('cancel')}>
            <XCircle /> {t('stop.cancel')}
          </Button>
        ) : null}
        {offer ? (
          <Button size="sm" onClick={() => decide('reactivate')} disabled={!stop.can_reactivate} title={blocked ?? undefined}>
            <RefreshCw /> {t('stop.reactivate')}
          </Button>
        ) : null}
      </div>
      <Dialog open={open !== null} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent>
          <form onSubmit={(e) => void submit(e)} className="grid gap-4">
            <DialogHeader>
              <DialogTitle>{open === 'cancel' ? t('stop.cancelTitle') : open === 'reactivate' ? t('stop.reactivateTitle') : t('stop.retryTitle')}</DialogTitle>
              <DialogDescription>
                {open === 'cancel'
                  ? t(cancelText.body)
                  : open === 'reactivate' && offer
                    ? t('stop.reactivateBody', {
                        amount: formatMoney({ amount: offer.amount, currency: offer.currency }),
                        available: formatMoney({ amount: offer.available, currency: offer.currency }),
                      })
                    : waiting
                      ? t('stop.retryWaitingBody')
                      : t('stop.retryBody')}
              </DialogDescription>
            </DialogHeader>
            <label className="grid gap-1.5 text-sm">
              <span className="font-medium">{t('stop.reason')}</span>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} required maxLength={500} />
            </label>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(null)}>
                {t('stop.back')}
              </Button>
              <Button type="submit" disabled={busy || !reason.trim()} variant={open === 'cancel' ? 'destructive' : 'default'}>
                {open === 'cancel' ? t('stop.cancel') : open === 'reactivate' ? t('stop.reactivate') : t('stop.retry')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** The verification a subject still lacks (owner decision 2026-10-01: an individual by eKYC
 * only, an organisation by its subject declaration), with a link to the subject declaration. */
export function VerificationGap({ v, subjectId, link }: { v: VerificationAnswer; subjectId: string; link: boolean }) {
  const { t } = useTranslation('domains');
  return (
    <div className="grid gap-1" data-readiness-verification={v.state}>
      <span className="text-xs font-medium text-muted-foreground">
        {t('subject.readiness.verification.label')} · {t(`subject.readiness.verification.${v.required}`)}
      </span>
      <span className="text-sm">{t(`subject.readiness.verification.states.${v.state as 'MISSING'}`, { defaultValue: v.message ?? '' })}</span>
      {v.required === 'SUBJECT_DECLARATION' && link ? (
        <Link to={`/subjects/${encodeURIComponent(subjectId)}?tab=declarations`} className="text-sm text-primary hover:underline">
          {t('subject.readiness.verification.open')}
        </Link>
      ) : null}
    </div>
  );
}

/**
 * In the registration dialog, before any order is placed: what the connection of `domain`'s zone
 * would refuse in the chosen registrant's data, each gap once (with links to edit the subject),
 * and the verification the subject still lacks — not checked at order: the registration waits
 * for it ("Chờ định danh").
 */
export function RegistrantReadiness({ subjectId, domain }: { subjectId: string; domain: string }) {
  const { t } = useTranslation('domains');
  const r = useApi((signal) => (subjectId ? api.getSubjectRegistryReadiness(subjectId, { signal }) : Promise.resolve({ registries: [] })), [subjectId]);
  if (!subjectId || r.status !== 'success') return null;
  const g = readinessFor(r.data, domain);
  if (!g || g.ready) return null;
  const problems = [...g.baseline, ...g.extras.flatMap((x) => x.problems)];
  return (
    <div className="grid gap-3 rounded-md border border-warning/40 bg-warning/5 p-3">
      {problems.length ? (
        <div className="grid gap-2">
          <div className="text-sm font-medium">{t('stop.incompleteTitle')}</div>
          <RegistrantProblems problems={problems} />
        </div>
      ) : null}
      {g.verification ? (
        <div className="grid gap-2">
          <div className="text-sm font-medium">{t('stop.verificationTitle')}</div>
          <VerificationGap v={g.verification} subjectId={subjectId} link />
        </div>
      ) : null}
    </div>
  );
}

/**
 * The fields of a `REGISTRANT_INCOMPLETE` refusal (quote / order), with links to edit the subject.
 * `hideExtra`: the TLD's extra fields are shown under their own inputs in the form.
 */
export function RegistrantIncomplete({ error, hideExtra = false }: { error: unknown; hideExtra?: boolean }) {
  if (!isApiError(error) || error.code !== 'REGISTRANT_INCOMPLETE') return null;
  const all = (error.details as { fields?: RegistrantProblem[] } | undefined)?.fields ?? [];
  const fields = hideExtra ? all.filter((p) => !isExtraProblem(p)) : all;
  return fields.length ? <RegistrantProblems problems={fields} /> : null;
}
